import React, { createContext, useContext, useState, useCallback } from 'react';
import { en } from '../locales/en';
import { fr } from '../locales/fr';
import { supabase } from '../lib/supabase';

const locales = { en, fr };

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('app_language') || 'en';
  });

  // persist=true means save to DB (user-initiated toggle)
  // persist=false means only update local state (profile sync on login)
  const setLanguage = useCallback(async (lang, persist = true) => {
    localStorage.setItem('app_language', lang);
    setLanguageState(lang);

    if (persist) {
      // Persist to DB if user is authenticated
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          await supabase?.from('user_profiles')?.update({ language: lang })?.eq('id', user?.id);
        }
      } catch (err) {
        // Non-critical: localStorage already saved
        console.warn('Could not persist language to DB:', err);
      }
    }
  }, []);

  const t = useCallback((key, vars) => {
    const keys = key?.split('.');
    let value = locales?.[language];
    for (const k of keys) {
      if (value == null) return key;
      value = value?.[k];
    }
    if (value == null) return key;
    if (vars && typeof value === 'string') {
      return value?.replace(/\{(\w+)\}/g, (_, name) =>
        vars?.[name] !== undefined ? vars?.[name] : `{${name}}`
      );
    }
    return value;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
